import type React from "react";

import { Input } from "../../../components/Input";

import { LoginCountrySelect } from "./LoginCountrySelect";
import { LoginGoogleButton } from "./LoginGoogleButton";
import type { LoginViewProps } from "./LoginView";
import type { LoginViewStateResult } from "./useLoginViewState";

import { Button } from "@/components/Button";
import { countryName, type CountryCode } from "@/config/countries";

type Props = Pick<
  LoginViewProps,
  | "step"
  | "formattedPhone"
  | "otp"
  | "localError"
  | "resendTimer"
  | "mockOtp"
  | "otpSentText"
  | "selectionSummary"
  | "showTenantSelection"
  | "showContextSelection"
  | "tenantOptions"
  | "contextOptions"
  | "selectedTenantId"
  | "selectedContextKey"
  | "phoneRef"
  | "otpRef"
  | "setPhoneFromInput"
  | "setOtpFromInput"
  | "setSelectedTenantId"
  | "setSelectedContextKey"
  | "handleResend"
  | "loading"
> &
  LoginViewStateResult;

export function LoginAuthCard({
  step,
  formattedPhone,
  otp,
  localError,
  resendTimer,
  mockOtp,
  otpSentText,
  selectionSummary,
  showTenantSelection,
  showContextSelection,
  tenantOptions,
  contextOptions,
  selectedTenantId,
  selectedContextKey,
  phoneRef,
  otpRef,
  setPhoneFromInput,
  setOtpFromInput,
  setSelectedTenantId,
  setSelectedContextKey,
  handleResend,
  loading,
  isSendingOtp,
  isVerifyingOtp,
  isActivatingSession,
  canResend,
  handleSendOtpClick,
  handleLoginClick,
  handleContinueClick,
  handlePhoneKeyDownLocal,
  handleOtpKeyDownLocal,
  handleChangeNumberLocal,
  methodTabs,
  googleEnabled,
  country,
  setCountry,
  loginTitle,
  loginSubtitle,
}: Props & {
  methodTabs?: React.ReactNode;
  googleEnabled?: boolean;
  country: CountryCode;
  setCountry: (country: CountryCode) => void;
  loginTitle?: string;
  loginSubtitle?: string;
}) {
  return (
    <section className="login-auth" aria-labelledby="login-title">
      <div className="login-card">
        <div className="login-card-heading">
          <h2 id="login-title">{loginTitle ?? "Welcome back"}</h2>
          <p className="login-description">
            {loginSubtitle ?? "Please sign in to continue to your dashboard"}
          </p>
        </div>

        {methodTabs}

        <div className="login-fields">
          <div>
            <label htmlFor="login-phone" className="login-label">
              Mobile number
            </label>
            <div className="login-phone-row">
              <LoginCountrySelect
                value={country}
                disabled={step !== "enter"}
                onChange={setCountry}
              />
              <div className="min-w-0">
                <Input
                  id="login-phone"
                  ref={phoneRef}
                  value={formattedPhone}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPhoneFromInput(e.target.value)
                  }
                  placeholder={`Mobile number (${countryName(country)})`}
                  onKeyDown={handlePhoneKeyDownLocal}
                  inputMode="numeric"
                  autoComplete="tel-national"
                  aria-describedby={
                    step === "enter" && localError ? "login-error" : undefined
                  }
                  disabled={step !== "enter"}
                />
              </div>
            </div>
            <Button
              onClick={handleSendOtpClick}
              loading={loading && step === "enter"}
              disabled={step !== "enter" || (loading && step === "enter") || isSendingOtp}
              className="login-send-button"
            >
              Send OTP
            </Button>
          </div>

          <div>
            <label htmlFor="login-otp" className="login-label">
              Enter OTP
            </label>
            <div>
              <Input
                id="login-otp"
                ref={otpRef}
                value={otp}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setOtpFromInput(e.target.value)
                }
                placeholder="6-digit OTP"
                onKeyDown={handleOtpKeyDownLocal}
                inputMode="numeric"
                autoComplete="one-time-code"
                aria-describedby={
                  step === "otp"
                    ? [otpSentText && "login-otp-hint", localError && "login-error"]
                        .filter(Boolean)
                        .join(" ") || undefined
                    : undefined
                }
                disabled={step !== "otp"}
                className="login-otp-input"
              />
            </div>
          </div>

          {step === "otp" && otpSentText && (
            <div id="login-otp-hint" className="login-helper" role="status">
              {otpSentText}
            </div>
          )}

          {step === "select" && selectionSummary && (
            <div className="space-y-4">
              <div className="login-helper">{selectionSummary}</div>
              {showTenantSelection && (
                <div>
                  <label htmlFor="login-tenant" className="login-label">
                    Tenant
                  </label>
                  <select
                    id="login-tenant"
                    value={selectedTenantId}
                    onChange={(e) => setSelectedTenantId(e.target.value)}
                  >
                    {tenantOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {showContextSelection && (
                <div>
                  <label htmlFor="login-context" className="login-label">
                    Role context
                  </label>
                  <select
                    id="login-context"
                    value={selectedContextKey}
                    onChange={(e) => setSelectedContextKey(e.target.value)}
                  >
                    {contextOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {contextOptions.length > 0 && (
                    <div className="login-helper mt-2">
                      {
                        contextOptions.find((o) => o.value === selectedContextKey)
                          ?.description
                      }
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {step === "otp" && mockOtp && (
            <div className="login-helper">Test OTP: {mockOtp}</div>
          )}

          <div className="login-secondary-actions">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleResend}
              disabled={!canResend}
              className="login-text-button"
            >
              {resendTimer > 0 ? `Resend OTP (${resendTimer}s)` : "Resend OTP"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleChangeNumberLocal}
              className="login-text-button"
            >
              Change number
            </Button>
          </div>

          <div className="login-terms">
            By signing in you agree to the Terms and Privacy Policy
          </div>

          {localError && (
            <div id="login-error" className="login-error" role="alert">
              {localError}
            </div>
          )}

          <Button
            onClick={step === "select" ? handleContinueClick : handleLoginClick}
            loading={loading && (step === "otp" || step === "select")}
            disabled={
              (step === "otp" && isVerifyingOtp) ||
              (step === "select" && isActivatingSession) ||
              (step !== "otp" && step !== "select")
            }
            className="w-full"
          >
            {step === "select" ? "Continue to Dashboard" : "Login"}
          </Button>

          {/* Hidden mid-flow: once a code has been sent or a tenant is being chosen, switching to a
              different sign-in method would abandon work already in progress. */}
          {googleEnabled && step === "enter" && (
            <>
              <div className="login-divider">
                <span>or</span>
              </div>
              <LoginGoogleButton disabled={loading} />
            </>
          )}
        </div>
      </div>
    </section>
  );
}
